import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "./Button";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Hata yakalandığında çizilecek içerik. Verilmezse varsayılan kurtarma ekranı. */
  fallback?: (yeniden: () => void) => ReactNode;
  /** Telemetri kancası — hatayı raporlamak isteyen uygulama buradan alır. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Render sırasında patlayan bir ağacı yakalar ve kullanıcıya çıkış yolu verir
 * (CLOS-95 `[056]` — *"hata durumunda güvenli yeniden deneme veya yönlendirme bulunur"*).
 *
 * ### Neden sınıf bileşeni
 *
 * React 19'da hâlâ hata sınırı için kanca yok; `componentDidCatch`/`getDerivedStateFromError`
 * yalnız sınıflarda çalışıyor. Bu dosyadaki tek istisnanın sebebi budur.
 *
 * ### Neden mesaj gösterilmiyor
 *
 * Kullanıcıya `error.message` basılmıyor: içinde yığın izi, iç modül adı ya da teknik
 * ayrıntı olabilir ve bunlar `[067]`'nin *"hata mesajı stack trace, altyapı adı veya
 * hassas teknik bilgi sızdırmaz"* kuralına aykırı. Ayrıntı `onError` ile telemetriye
 * gider; ekranda yalnız yapılabilecek şey durur.
 *
 * ### Sınırı
 *
 * Yalnız RENDER hatalarını yakalar. Olay işleyicilerindeki ve `async` yollardaki hatalar
 * buraya düşmez — onlar `ApiRequestError` ve toast yoluyla ele alınıyor.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly yenidenDene = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.yenidenDene);

    return (
      <div
        role="alert"
        className="flex min-h-64 flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <p className="text-sm font-medium text-fg">Bu bölüm yüklenemedi</p>
        <p className="max-w-sm text-sm text-fg-muted">
          Beklenmeyen bir sorun oluştu. Yeniden deneyebilir ya da sayfayı yenileyebilirsiniz.
        </p>
        <Button onClick={this.yenidenDene}>Yeniden dene</Button>
      </div>
    );
  }
}
